
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Edit, Trash2, Package } from 'lucide-react';
import { useEspacesStockage, EspaceStockage } from '@/hooks/useEspacesStockage';
import { useTranslation } from 'react-i18next';

interface GestionEspacesStockageProps {
  selectedBateauId?: string;
}

const GestionEspacesStockage = ({ selectedBateauId }: GestionEspacesStockageProps) => {
  const { espaces, loading, ajouterEspace, modifierEspace, supprimerEspace } = useEspacesStockage();
  const { t } = useTranslation('inventory');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEspace, setEditingEspace] = useState<EspaceStockage | null>(null);
  const [formData, setFormData] = useState({
    nom: '',
    type: '',
    description: '',
    capacite: '',
  });

  const typesEspaces = [
    'Frigo',
    'Placard',
    'Coffre',
    'Cabine',
    'Soute',
    'Cockpit',
    'Pont',
    'Autre'
  ];

  const typeLabelKeys: Record<string, string> = {
    'Frigo': 'fridge',
    'Placard': 'cupboard',
    'Coffre': 'chest',
    'Cabine': 'cabin',
    'Soute': 'hold',
    'Cockpit': 'cockpit',
    'Pont': 'deck',
    'Autre': 'other',
  };
  const getTypeLabel = (type: string) => typeLabelKeys[type] ? t(`gestionEspaces.types.${typeLabelKeys[type]}`) : type;

  const resetForm = () => {
    setFormData({
      nom: '',
      type: '',
      description: '',
      capacite: '',
    });
    setEditingEspace(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nom || !formData.type) return;

    const espaceData = {
      bateau_id: selectedBateauId || null,
      nom: formData.nom,
      type: formData.type,
      description: formData.description || null,
      capacite: formData.capacite ? parseInt(formData.capacite) : null,
    };

    let result;
    if (editingEspace) {
      result = await modifierEspace(editingEspace.id, espaceData);
    } else {
      result = await ajouterEspace(espaceData);
    }

    if (!result.error) {
      resetForm();
      setIsDialogOpen(false);
    }
  };

  const handleEdit = (espace: EspaceStockage) => {
    setEditingEspace(espace);
    setFormData({
      nom: espace.nom,
      type: espace.type,
      description: espace.description || '',
      capacite: espace.capacite?.toString() || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm(t('gestionEspaces.confirmDelete'))) {
      await supprimerEspace(id);
    }
  };

  const espacesFiltered = selectedBateauId 
    ? espaces.filter(e => e.bateau_id === selectedBateauId)
    : espaces;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">{t('gestionEspaces.title')}</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-nautic text-white">
              <Plus className="h-4 w-4 mr-2" />
              {t('gestionEspaces.addSpace')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingEspace ? t('gestionEspaces.editSpaceTitle') : t('gestionEspaces.addSpaceTitle')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nom">{t('gestionEspaces.name')}</Label>
                <Input
                  id="nom"
                  value={formData.nom}
                  onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                  placeholder={t('gestionEspaces.namePlaceholder')}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">{t('gestionEspaces.type')}</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('gestionEspaces.chooseType')} />
                  </SelectTrigger>
                  <SelectContent>
                    {typesEspaces.map((type) => (
                      <SelectItem key={type} value={type}>
                        {getTypeLabel(type)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="capacite">{t('gestionEspaces.capacity')}</Label>
                <Input
                  id="capacite"
                  type="number"
                  value={formData.capacite}
                  onChange={(e) => setFormData(prev => ({ ...prev, capacite: e.target.value }))}
                  placeholder={t('gestionEspaces.capacityPlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{t('gestionEspaces.description')}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t('gestionEspaces.descriptionPlaceholder')}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit">
                  {editingEspace ? t('gestionEspaces.modify') : t('gestionEspaces.add')}
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  {t('gestionEspaces.cancel')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div>{t('gestionEspaces.loading')}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {espacesFiltered.map((espace) => (
            <Card key={espace.id}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{espace.nom}</CardTitle>
                    <p className="text-sm text-muted-foreground">{getTypeLabel(espace.type)}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(espace)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(espace.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {espace.capacite && (
                  <p className="text-sm text-muted-foreground">
                    {t('gestionEspaces.capacityDisplay', { capacity: espace.capacite })}
                  </p>
                )}
                {espace.description && (
                  <p className="text-sm mt-2">{espace.description}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {espacesFiltered.length === 0 && !loading && (
        <Card>
          <CardContent className="p-8 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {t('gestionEspaces.noSpaceConfigured')}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GestionEspacesStockage;
